// @mui
import { useSettingsContext } from 'src/components/settings';
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';

// ----------------------------------------------------------------------

export default function BetterSolutionSection() {
  const settings = useSettingsContext();
  const homePageData = useSelector((state) => state.website.homePage);
  const { fetchOnMount } = useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Section Headers
    mainHeading: "",
    mainHeadingColor: "#333333",
    // mainHeadingFontSize: "2.5rem",
    // mainHeadingFontWeight: "700",

    subHeading: "",
    subHeadingColor: "#8BC34A",
    // subHeadingFontSize: "1.2rem",
    // subHeadingFontWeight: "400",

    // Section Background
    // backgroundColor: "#ffffff",

    // Paragraph Content
    paragraphText: "",
    paragraphColor: "#666666",
    // paragraphFontSize: "16px",
    // paragraphLineHeight: "1.6",

    // Read More Settings
    readMoreText: "Read more",
    readLessText: "Read less",
    readMoreColor: "#8BC34A",
    showReadMore: true,
    paragraphPreviewLength: 300, // Characters to show before read more

    // Video Settings
    videoUrl: "",
    videoTitle: "",
    videoThumbnail: "",
    showVideoSection: true,

    // Layout Settings
    // contentAlignment: "left",
    // sectionPadding: "60px 0",
    // containerMaxWidth: "1200px"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    if (homePageData?.better_solution_section) {
      const BetterSolutionSection = JSON.parse(JSON.stringify(homePageData.better_solution_section));
      setFormData(prev => ({
        ...prev,
        ...BetterSolutionSection
      }));
    }
  }, [homePageData]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.home_page.hero_section,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(homePageData)),
            better_solution_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const ColorPreview = ({ color, size = 24 }) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid #ddd',
        display: 'inline-block',
        mr: 1
      }}
    />
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
         Better Solution Section
        </Typography>

        {/* Section Headers */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Headers Section</Typography>
          </Stack>

          {/* Main Heading */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Heading</Typography>
            <TextField
              fullWidth
              value={formData.mainHeading}
              onChange={(e) => updateFormData('mainHeading', e.target.value)}
              placeholder="Enter main heading"
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2">Color:</Typography>
                  <ColorPreview color={formData.mainHeadingColor} />
                  <TextField
                    type="color"
                    value={formData.mainHeadingColor}
                    onChange={(e) => updateFormData('mainHeadingColor', e.target.value)}
                    sx={{ width: 100 }}
                  />
                </Stack>
              </Grid>
              {/* <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Font Size"
                  value={formData.mainHeadingFontSize}
                  onChange={(e) => updateFormData('mainHeadingFontSize', e.target.value)}
                  placeholder="e.g., 2.5rem"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Font Weight"
                  value={formData.mainHeadingFontWeight}
                  onChange={(e) => updateFormData('mainHeadingFontWeight', e.target.value)}
                  placeholder="e.g., 700"
                />
              </Grid> */}
            </Grid>
          </Box>

          {/* Sub Heading */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Sub Heading</Typography>
            <TextField
              fullWidth
              value={formData.subHeading}
              onChange={(e) => updateFormData('subHeading', e.target.value)}
              placeholder="Enter sub heading"
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2">Color:</Typography>
                  <ColorPreview color={formData.subHeadingColor} />
                  <TextField
                    type="color"
                    value={formData.subHeadingColor}
                    onChange={(e) => updateFormData('subHeadingColor', e.target.value)}
                    sx={{ width: 100 }}
                  />
                </Stack>
              </Grid>
              {/* <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Font Size"
                  value={formData.subHeadingFontSize}
                  onChange={(e) => updateFormData('subHeadingFontSize', e.target.value)}
                  placeholder="e.g., 1.2rem"
                />
              </Grid> */}
              {/* <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Font Weight"
                  value={formData.subHeadingFontWeight}
                  onChange={(e) => updateFormData('subHeadingFontWeight', e.target.value)}
                  placeholder="e.g., 400"
                />
              </Grid> */}
            </Grid>
          </Box>

          {/* Background Color */}
          {/* <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Background Color</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ColorPreview color={formData.backgroundColor} />
              <TextField
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => updateFormData('backgroundColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box> */}
        </Box>

        {/* Paragraph Content */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Paragraph Content</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Paragraph Text</Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={formData.paragraphText}
              onChange={(e) => updateFormData('paragraphText', e.target.value)}
              placeholder="Enter the detailed paragraph content"
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Preview Length"
                  type="number"
                  value={formData.paragraphPreviewLength}
                  onChange={(e) => updateFormData('paragraphPreviewLength', parseInt(e.target.value))}
                  placeholder="Characters before read more"
                  helperText="Characters to show before 'Read more'"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2">Text Color:</Typography>
                  <ColorPreview color={formData.paragraphColor} />
                  <TextField
                    type="color"
                    value={formData.paragraphColor}
                    onChange={(e) => updateFormData('paragraphColor', e.target.value)}
                    sx={{ width: 80 }}
                  />
                </Stack>
              </Grid>
              {/* <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Font Size"
                  value={formData.paragraphFontSize}
                  onChange={(e) => updateFormData('paragraphFontSize', e.target.value)}
                  placeholder="e.g., 16px"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Line Height"
                  value={formData.paragraphLineHeight}
                  onChange={(e) => updateFormData('paragraphLineHeight', e.target.value)}
                  placeholder="e.g., 1.6"
                />
              </Grid> */}

            </Grid>
          </Box>
        </Box>

        {/* Read More Settings */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Read More Settings</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Read More Text"
                value={formData.readMoreText}
                onChange={(e) => updateFormData('readMoreText', e.target.value)}
                placeholder="Read more"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Read Less Text"
                value={formData.readLessText}
                onChange={(e) => updateFormData('readLessText', e.target.value)}
                placeholder="Read less"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">Link Color:</Typography>
                <ColorPreview color={formData.readMoreColor} />
                <TextField
                  type="color"
                  value={formData.readMoreColor}
                  onChange={(e) => updateFormData('readMoreColor', e.target.value)}
                  sx={{ width: 100 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2">Show Read More:</Typography>
                <Button
                  variant={formData.showReadMore ? "contained" : "outlined"}
                  size="small"
                  onClick={() => updateFormData('showReadMore', !formData.showReadMore)}
                >
                  {formData.showReadMore ? "Enabled" : "Disabled"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Video Settings */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Video </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2">Show Video Section:</Typography>
                <Button
                  variant={formData.showVideoSection ? "contained" : "outlined"}
                  size="small"
                  onClick={() => updateFormData('showVideoSection', !formData.showVideoSection)}
                >
                  {formData.showVideoSection ? "Enabled" : "Disabled"}
                </Button>
              </Stack>
            </Grid>

            {formData.showVideoSection && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Video URL"
                    value={formData.videoUrl}
                    onChange={(e) => updateFormData('videoUrl', e.target.value)}
                    placeholder="Enter YouTube video URL"
                    helperText="Enter full YouTube URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Video Title"
                    value={formData.videoTitle}
                    onChange={(e) => updateFormData('videoTitle', e.target.value)}
                    placeholder="Enter video title (optional)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Video Thumbnail URL"
                    value={formData.videoThumbnail}
                    onChange={(e) => updateFormData('videoThumbnail', e.target.value)}
                    placeholder="Enter custom thumbnail URL (optional)"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>





        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
